export type ScoroAjaxResponse<
	Data extends Record<string, unknown> = Record<string, unknown>,
> = {
	status: 'OK';
	data: Data;
};

export const isListBlockResponse = (
	response: ScoroAjaxResponse
): response is ScoroAjaxResponse<{
	html: Record<string, string | undefined>;
}> => {
	return 'html' in response.data;
};

const isSubComponentResponse = (
	response: ScoroAjaxResponse
): response is ScoroAjaxResponse<{
	sub_component_content: string;
}> => {
	return 'sub_component_content' in response.data;
};

export const getSubComponentHTMLFromResponse = (
	response: ScoroAjaxResponse
) => {
	if (!isSubComponentResponse(response)) {
		return;
	}

	return response.data.sub_component_content;
};

declare global {
	interface Window {
		scoro: {
			// These type definitions are not complete, modify them as needed
			submitAjax: <
				ResponseData extends Record<string, unknown>,
				Params extends Record<string, unknown>,
			>(
				url: string,
				responseHandlerName: (
					params: Params,
					response: ScoroAjaxResponse<ResponseData>
				) => void,
				parameters: Params,
				dontStartLoading?: boolean,
				errorHandlerName?: (params: Params, response: unknown) => void
			) => void;
		};
	}
}

/**
 * A Promisified wrapper around the monolith `scoro.submitAjax` function for
 * making HTTP requests.
 */
export const submitAjax = <
	ResponseData extends Record<string, unknown>,
	Params extends Record<string, unknown> = Record<string, unknown>,
>(
	url: string,
	parameters: Params
): Promise<ScoroAjaxResponse<ResponseData>> => {
	return new Promise((resolve, reject) => {
		window.scoro.submitAjax<ResponseData, Params>(
			url,
			(_, response) => resolve(response),
			parameters,
			undefined,
			(_, error) => reject(error)
		);
	});
};

/**
 * Execute all script tags inside an element. Useful when adding some HTML from
 * monolith AJAX requests to the DOM with `.innerHTML` as `.innerHTML` does not
 * execute any scripts, but jQuery `.append()` does.
 *
 * Copied from https://stackoverflow.com/questions/2592092/executing-script-elements-inserted-with-innerhtml
 */
export const executeScriptsInElement = (element: HTMLElement) => {
	Array.from(element.querySelectorAll('script')).forEach(
		(oldScriptElement) => {
			const newScriptElement = document.createElement('script');

			Array.from(oldScriptElement.attributes).forEach((attr) => {
				newScriptElement.setAttribute(attr.name, attr.value);
			});

			const scriptText = document.createTextNode(
				oldScriptElement.innerHTML
			);
			newScriptElement.appendChild(scriptText);

			oldScriptElement.parentNode?.replaceChild(
				newScriptElement,
				oldScriptElement
			);
		}
	);
};
